import { Stack, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { TeamStrategyFilterByOption } from "src/constants";
import { ContainerGrid, useDrawer } from "../../../../components";
import { usePlanToCsv } from "../../../../hooks";
import { IPlan } from "../../../../models";
import { useGetPlanTree } from "../hooks";
import { Blocks } from "./Blocks";
import { PlanHeader } from "./PlanHeader";
import { PlanVersionsDrawerContent } from "./PlanVersionsDrawerContent";

interface IProps {
  teamName: string;
  teamSlug: string;
  planGuid: string;
  headerContent: (args: {
    isLoading: boolean;
    isPastView: boolean;
    handleVersionsClicked: () => void;
    handleExportClicked: (filename: string) => void;
    plan?: IPlan;
    dateTimeLocal?: string | null;
    dateTimeLocalUtcOffsetInMinutes?: number | null;
  }) => React.ReactNode;
  canEdit?: boolean;
  meetingView?: boolean;
  tableView?: boolean;
  filters?: Record<TeamStrategyFilterByOption, string[]>;
  onViewToggled: (tableView: boolean) => void;
}

const Plan = (props: IProps) => {
  const [queryParams] = useSearchParams();
  const [dateTimeLocal, setDateTimeLocal] = useState<string>();
  const [dateTimeLocalUtcOffsetInMinutes, setDateTimeLocalUtcOffsetInMinutes] = useState<number>();
  const [isPastView, setIsPastView] = useState(false);
  const { plan, setPlan, isLoading, fetchActionItemSubitems } = useGetPlanTree(
    props.planGuid,
    dateTimeLocal,
    dateTimeLocalUtcOffsetInMinutes,
    {
      filters: isPastView ? {} : props.filters,
    },
    {
      filters: isPastView ? {} : props.filters,
    },
  );
  const { convertAndDownloadPlanAsCsv } = usePlanToCsv(props.planGuid);
  const [isTableView, setIsTableView] = useState(true);
  const container = React.useRef<HTMLDivElement>(null);
  const { showDrawer } = useDrawer();
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  useEffect(() => {
    const date = queryParams.get("date");
    const offset = queryParams.get("offset");

    setDateTimeLocal(date ?? undefined);
    setDateTimeLocalUtcOffsetInMinutes(offset ? Number(offset) : undefined);
    setIsPastView(date != null && offset != null);
  }, [queryParams, plan]);

  useEffect(() => {
    if (isMdUp) {
      setIsTableView(props.tableView ?? false);
    } else {
      setIsTableView(false);
    }
  }, [isMdUp, props.tableView, props.teamSlug]);

  function handleViewToggled(value: boolean) {
    setIsTableView(value);
    props.onViewToggled(value);
  }

  const headerArgs = {
    isLoading: isLoading,
    isPastView: isPastView,
    handleVersionsClicked: () =>
      showDrawer({
        content: <PlanVersionsDrawerContent planGuid={props.planGuid} planLastModified={plan?.lastModifiedUtc} />,
      }),
    handleExportClicked: (filename: string) => convertAndDownloadPlanAsCsv(filename),
    plan: plan,
    dateTimeLocal: dateTimeLocal,
    dateTimeLocalUtcOffsetInMinutes: dateTimeLocalUtcOffsetInMinutes,
  };

  return (
    <Stack ref={container} spacing={{ xs: 0.75, md: 1 }}>
      <PlanHeader
        planGuid={props.planGuid}
        content={props.headerContent(headerArgs)}
        isTableView={isTableView}
        isPastView={isPastView}
        onViewToggled={handleViewToggled}
      />

      {!isLoading && plan !== undefined && (
        <ContainerGrid container spacing={{ xs: 0.75, md: 1 }}>
          <Blocks
            teamName={props.teamName}
            teamSlug={props.teamSlug}
            plan={plan}
            isTableView={isTableView}
            isPastView={isPastView}
            canEdit={props.canEdit}
            meetingView={props.meetingView}
            onActionItemSortOrderChanged={(updatedPlan) => setPlan(updatedPlan)}
            fetchActionItemSubitems={fetchActionItemSubitems}
          />
        </ContainerGrid>
      )}
    </Stack>
  );
};

export { Plan };
